// tslint:disable:no-trailing-whitespace
// tslint:disable:member-ordering
import { BaseEntity } from '../base-entity';
import { ActiveStatus } from './active-status';
import { EOQType } from './eoq-type';
import { Manufacturer } from './manufacturer';
import { ManufacturerProductTypeConfig } from './manufacturer-product-type-config';
import { Product } from './product';
import { ProductTypeAlt } from './product-type-alt';
import { ProductTypeFeatureMap } from './product-type-feature-map';
import { ProductTypeImage } from './product-type-image';
import { ProductTypeTagMap } from './product-type-tag-map';
import { StagedProductType } from './staged-product-type';
import { ActiveStatusEnum } from './enums';

/// <code-import> Place custom imports between <code-import> tags
import _ from "lodash";
import { Validator } from "breeze-client";
import { EntityPropertyAnnotation, EntityTypeAnnotation } from "../entity-type-annotation";
/// </code-import>

/// <module-code> Place module level code between <module-code> tags

/// </module-code>

export class ProductType extends BaseEntity  {

  /// <code> Place custom code between <code> tags
  /**
   * Held as a FRACTION and shown as a percentage, so 0.15 on the entity is 15 on the screen and
   * the real ceiling is 1. Said in percent, because that is the only form anybody sees - a
   * message about 1 would be about a number nobody typed.
   *
   * Empty is allowed: the column is nullable and a style that has never had an EOQ worked out
   * has nothing to put here.
   */
  static safetyStockPctValidator = new Validator(
    "safetyStockPct",
    (value: any) => value == null || (Number.isFinite(value) && value >= 0 && value <= 1),
    { messageTemplate: "Safety stock must be between 0 and 100 percent." }
  );

  /**
   * Required and maximum length come from the metadata - Style is varchar(30), Description is
   * varchar(60) and NOT NULL - so Breeze already validates those and qb-field already shows
   * them. Only the rules the metadata cannot express are declared here.
   *
   * All four columns below are NULLABLE except LeadTimeDays, so these validators speak about the
   * VALUE and never about whether there is one. Requiring a value is the metadata's job.
   */
  static getEntityTypeAnnotation(): EntityTypeAnnotation {
    const propAnnotations = [
      new EntityPropertyAnnotation("leadTimeDays", {
        displayName: "Lead time",
        validators: [BaseEntity.optionalNotNegativeValidator],
      }),
      new EntityPropertyAnnotation("orderingCostAmt", {
        displayName: "Ordering cost",
        validators: [BaseEntity.optionalNotNegativeValidator],
      }),
      new EntityPropertyAnnotation("unitCarryingCostPerYearAmt", {
        displayName: "Carrying cost per year",
        validators: [BaseEntity.optionalNotNegativeValidator],
      }),
      new EntityPropertyAnnotation("safetyStockPerYearPct", {
        displayName: "Safety stock",
        validators: [ProductType.safetyStockPctValidator],
      }),
    ];

    return new EntityTypeAnnotation({
      validators: [],
      propertyAnnotations: propAnnotations,
    });
  }

  getOrderedProductTypeFeatureMaps() {
    return _.orderBy(this.productTypeFeatureMaps, (x) => x.displayOrder);
  }

  getFeatureNamesExtract() {
    return this.getOrderedProductTypeFeatureMaps()
      .map((ptf) => ptf.feature.name)
      .join("; ");
  }

  get featureNamesExtract() {
    return this.getFeatureNamesExtract();
  }

  get name() {
    if (this.manufacturer) {
      return this.manufacturer.name + " / " + this.description + " / " + this.style;
    } else {
      return "not yet available";
    }
  }

  get productTypeAlts() {
    return this.productTypeAltProductTypes;
  }

  get safetyStockPerYearPctFormatted() {
    return this.safetyStockPerYearPct * 100;
  }
  set safetyStockPerYearPctFormatted(value) {
    this.safetyStockPerYearPct = value / 100;
  }
  /// </code>

  // Generated code. Do not place code below this line.
  /** Int32 key */
  id!: number;
  /** Enum FK activeStatus */
  activeStatusId!: ActiveStatusEnum;
  /** Boolean */
  allowReorderFeatureChoices!: boolean;
  /** String(60) */
  description!: string;
  /** String */
  descriptionExt?: string;
  /** DateTimeOffset */
  eOQModTs?: Date;
  /** String(10) FK eOQType */
  eOQTypeId!: string;
  /** Int32 */
  iTM_COL_KEY!: number;
  /** Boolean */
  isFeedEnabled!: boolean;
  /** Int32 */
  leadTimeDays!: number;
  /** Int32 FK manufacturer */
  manufacturerId!: number;
  /** DateTimeOffset */
  modTs!: Date;
  /** String(4) */
  modUserInit!: string;
  /** Decimal */
  orderingCostAmt?: number;
  /** Binary */
  rowVersion!: any;
  /** Decimal */
  safetyStockPerYearPct?: number;
  /** String(30) */
  style!: string;
  /** Decimal */
  unitCarryingCostPerYearAmt?: number;
  /** FK activeStatusId */
  activeStatus!: ActiveStatus;
  /** FK eOQTypeId */
  eOQType!: EOQType;
  /** FK manufacturerId */
  manufacturer!: Manufacturer;
  /** Inv FK productTypeId */
  manufacturerProductTypeConfigs!: ManufacturerProductTypeConfig[];
  /** Inv FK altProductTypeId */
  productTypeAltAltProductTypes!: ProductTypeAlt[];
  /** Inv FK productTypeId */
  productTypeAltProductTypes!: ProductTypeAlt[];
  /** Inv FK productTypeId */
  productTypeFeatureMaps!: ProductTypeFeatureMap[];
  /** Inv FK productTypeId */
  productTypeImages!: ProductTypeImage[];
  /** Inv FK productTypeId */
  productTypeTagMaps!: ProductTypeTagMap[];
  /** Inv FK productTypeId */
  products!: Product[];
  /** Inv FK productTypeId */
  stagedProductTypes!: StagedProductType[];
}

